/**
 * The new chat panel: a chat, laid out from the shared UI kit.
 *
 * It reads the chat it is handed (`ChatStoreProvider`) and turns each entry of
 * the conversation into kit pieces. It has no styling of its own — anything
 * visual belongs in `src/ui-kit/chat`. Results use the existing table, chart,
 * download and call cards until those move into the kit too.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button } from '@mui/material';
import {
  ApprovalCard,
  CHAT_READING_WIDTH,
  ChatComposer,
  ChatContextChips,
  ChatEmptyState,
  ChatHeader,
  ChatMessage,
  ChatMessageActions,
  ChatMessageList,
  ChatSourceNote,
  WorkbookCard,
  ChatWorkSteps,
  EmptyState,
  FileDropOverlay,
  Markdown,
  DownloadButton,
  Modal,
  XlsxPreview,
  type ChatStep,
  type ContextChip,
} from '../../ui-kit';
import { useChatStore, useChatStoreApi } from '../core/ChatStoreContext';
import type { ApprovalAction } from '../core/approvals';
import {
  copilotAuditHasDataTrail,
  type CopilotMessage,
  type CopilotStep,
  type WorkspaceArtifact,
} from '../../pages/copilot/stores/useCopilotStore';
import { useCopilotStreamingStatus } from '../../pages/copilot/hooks/useCopilotStreamingStatus';
import { getArtifactHydrationState, hydrateArtifactByRef } from '../../pages/copilot/hooks/useArtifactHydration';
import ResultView from './ResultView';
import { downloadWorkbook, fetchWorkbookPreview, type WorkbookPreview } from '../../utils/workbookExport';
import type { Workbook } from '../../types/workbook';
import SqlApproval from './SqlApproval';
import DownloadCard from '../../pages/copilot/components/DownloadCard';
import VoiceCallCard from '../../pages/copilot/components/VoiceCallCard';
import VoiceCallApprovalCard from '../../pages/copilot/components/VoiceCallApprovalCard';
import KnowledgePill from '../../pages/copilot/components/KnowledgePill';
import { AuditDialog, HistoricalAttachmentChips } from './AuditTrail';
import type { ChatPlace } from './defineChatPlace';

/** What the engine appends to an answer the analyst stopped. */
const STOPPED_MARK = '*(Stopped by analyst)*';
const WAITING_PLACEHOLDER = 'Review & approve the SQL above…';
const WORKING_PLACEHOLDER = 'Answering — ask your next question once this finishes';
const WAITING_PLACEHOLDER_PANE = 'Review & approve the SQL in the results pane…';

export interface ChatPanelV2Props {
  place: ChatPlace;
  /** Header title; also the welcome title when the place gives none of its own. */
  title: string;
  subtitle?: string;
  /** Buttons in the header: open full size, close. */
  headerActions?: React.ReactNode;
  onSend: (text: string) => void;
  onStop: () => void;
  onSQLApproval?: (action: ApprovalAction, editedSql?: string, reason?: string) => void;
  onVoiceCallApprove?: () => void;
  onVoiceCallEditNumber?: (number: string) => void;
  onVoiceCallReject?: (reason: string) => void;
  onAddToDashboard?: (artifact: WorkspaceArtifact) => void;
  /** Load a result's rows; defaults to loading them into this chat's store. */
  onArtifactHydrate?: (artifactId: string) => void;
  /** Where a place can send someone to do what it can't, e.g. approve a call. */
  onOpenFull?: () => void;
  contextChips?: { base?: string; chips: ContextChip[]; onRemove?: (key: string) => void };
  /** Replaces the plain header, e.g. with the Copilot page's thread bar. */
  header?: React.ReactNode;
  /** Inside the composer, before the text: an attach button. */
  composerStart?: React.ReactNode;
  /** Inside the composer, above the text: the files attached so far. */
  composerFiles?: React.ReactNode;
  /** One line above the composer offering a next step, e.g. Save as Agent. */
  composerNotice?: React.ReactNode;
  /** Under the composer, e.g. how much of the conversation the agent still sees. */
  composerFooter?: React.ReactNode;
  /** Why sending is held back, e.g. files still processing. */
  sendBlockedReason?: string;
  /** Takes files dragged onto the panel. Leave out to turn drag and drop off. */
  onFilesDropped?: (files: File[]) => void;
  /** What can be dropped, shown while dragging. */
  dropHint?: string;
}

const toSteps = (statuses?: (string | CopilotStep)[]): ChatStep[] =>
  (statuses ?? []).map((s) => (typeof s === 'string' ? { text: s } : { text: s.text, receipt: s.receipt }));

const renderStep = (text: string) => <Markdown variant="compact">{text}</Markdown>;

function copyText(text: string) {
  return navigator.clipboard?.writeText(text).catch(() => undefined);
}

export default function ChatPanelV2({
  place,
  title,
  subtitle,
  headerActions,
  onSend,
  onStop,
  onSQLApproval,
  onVoiceCallApprove,
  onVoiceCallEditNumber,
  onVoiceCallReject,
  onAddToDashboard,
  onArtifactHydrate,
  onOpenFull,
  contextChips,
  header,
  composerStart,
  composerFiles,
  composerNotice,
  composerFooter,
  sendBlockedReason,
  onFilesDropped,
  dropHint,
}: ChatPanelV2Props) {
  const store = useChatStoreApi();
  const messages = useChatStore((s) => s.messages);
  const timeline = useChatStore((s) => s.timeline);
  const workspaceTabs = useChatStore((s) => s.workspaceTabs);
  const isStreaming = useChatStore((s) => s.isStreaming);
  const streamingContent = useChatStore((s) => s.streamingContent);
  const streamingStatuses = useChatStore((s) => s.streamingStatuses);
  const streamingStartedAt = useChatStore((s) => s.streamingStartedAt);
  const pendingSql = useChatStore((s) => s.pendingSQLApproval);
  const pendingCall = useChatStore((s) => s.pendingVoiceCallApproval);
  const downloadOffers = useChatStore((s) => s.downloadOffers);
  const workbooks = useChatStore((s) => s.workbooks);
  const voiceCallSummaries = useChatStore((s) => s.voiceCallSummaries);
  const openVoiceCall = useChatStore((s) => s.openVoiceCall);
  const isLoadingHistory = useChatStore((s) => s.isLoadingHistory);
  const threadId = useChatStore((s) => s.threadId);
  const inputText = useChatStore((s) => s.inputText);
  const setInputText = useChatStore((s) => s.setInputText);
  const streamStatus = useCopilotStreamingStatus();
  const approvalsInPane = place.results === 'sidePane' && place.approvals === 'pane';

  const [auditId, setAuditId] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const hasFiles = (e: React.DragEvent) => Array.from(e.dataTransfer?.types || []).includes('Files');
  const dropHandlers = onFilesDropped
    ? {
        onDragEnter: (e: React.DragEvent) => { if (hasFiles(e)) setDragging(true); },
        onDragOver: (e: React.DragEvent) => {
          e.preventDefault();
          if (hasFiles(e)) { e.dataTransfer.dropEffect = 'copy'; setDragging(true); }
        },
        onDragLeave: (e: React.DragEvent) => {
          // Only when leaving the panel, not moving between its children.
          const to = e.relatedTarget as Node | null;
          if (!to || !(e.currentTarget as HTMLElement).contains(to)) setDragging(false);
        },
        onDrop: (e: React.DragEvent) => {
          e.preventDefault();
          setDragging(false);
          if (e.dataTransfer?.files?.length) onFilesDropped(Array.from(e.dataTransfer.files));
        },
      }
    : {};
  const hydrate = onArtifactHydrate ?? ((id: string) => void hydrateArtifactByRef(id, store.getState()));

  // Say "answer ready" once to screen readers when a turn ends.
  const [announcement, setAnnouncement] = useState('');
  const wasStreaming = useRef(isStreaming);
  useEffect(() => {
    if (wasStreaming.current && !isStreaming) {
      setAnnouncement(pendingSql ? 'A query is waiting for your approval' : 'Answer ready');
    }
    wasStreaming.current = isStreaming;
  }, [isStreaming, pendingSql]);

  // With results in a side pane, the conversation keeps only what was said,
  // files offered and calls made. The page lists and shows the results, and
  // approvals too when they go there, so the chat doesn't repeat them.
  const resultsElsewhere = place.results === 'sidePane';
  const entries = useMemo(
    () =>
      timeline.filter((e) => {
        if (e.type === 'streaming_assistant') return false;
        if (resultsElsewhere && e.type === 'artifact') return false;
        if (approvalsInPane) return e.type !== 'sql_approval' && e.type !== 'voice_call_approval';
        return true;
      }),
    [timeline, approvalsInPane, resultsElsewhere],
  );
  // Group each reply with what arrived while it was being worked on. The
  // timeline records a result the moment it lands and the reply only when it
  // finishes, so drawn in order a turn reads "result, then its steps, then its
  // text". A reply shows as steps → results → answer instead. Whatever came
  // after the last reply belongs to the turn still running, if there is one.
  //
  // A reply that paused part way — for a query approval, say — is saved as a
  // message with steps but no answer, and the reply that carries on after the
  // approval as another. They are one answer, so their steps join into one
  // "Worked" line and their results into one group.
  const turns = useMemo(() => {
    type Entry = (typeof entries)[number];
    type Block = { entry: Entry; i: number };
    type Held = { message: CopilotMessage; within: Block[]; i: number };
    const rows: Array<{ kind: 'entry'; entry: Entry; i: number } | ({ kind: 'message' } & Held)> = [];
    const state: { pending: Block[]; held: Held | null } = { pending: [], held: null };
    const flush = () => {
      if (state.held) rows.push({ kind: 'message', ...state.held });
      state.held = null;
      state.pending.forEach((p) => rows.push({ kind: 'entry', ...p }));
      state.pending = [];
    };
    entries.forEach((entry, i) => {
      if (entry.type !== 'user' && entry.type !== 'assistant') {
        state.pending.push({ entry, i });
        return;
      }
      const message = messages.find((m) => m.id === entry.messageId);
      if (!message) return;
      // A question, or a note like a saved definition, closes off what came before.
      if (entry.type === 'user' || message.type) {
        flush();
        rows.push({ kind: 'message', message, within: [], i });
        return;
      }
      let merged = message;
      let within = state.pending;
      if (state.held) {
        const took = (state.held.message.durationSec ?? 0) + (message.durationSec ?? 0);
        merged = {
          ...message,
          statuses: [...(state.held.message.statuses ?? []), ...(message.statuses ?? [])],
          durationSec: took || undefined,
        };
        within = [...state.held.within, ...state.pending];
        state.held = null;
      }
      state.pending = [];
      if (merged.content?.trim()) rows.push({ kind: 'message', message: merged, within, i });
      else state.held = { message: merged, within, i };
    });
    return { rows, held: state.held, trailing: state.pending };
  }, [entries, messages]);

  const orphans = useMemo(() => {
    const placed = new Set(
      timeline.filter((e) => e.type === 'user' || e.type === 'assistant').map((e) => (e as { messageId: string }).messageId),
    );
    return timeline.length ? messages.filter((m) => !placed.has(m.id)) : messages;
  }, [timeline, messages]);

  const hasLiveTurn = isStreaming || streamingStatuses.length > 0 || !!streamingContent;
  const empty = !isLoadingHistory && messages.length === 0 && timeline.length === 0 && !hasLiveTurn;
  const userTurns = messages.filter((m) => m.role === 'user').length;
  const followKey = `${timeline.length}|${messages.length}|${streamingStatuses.length}|${streamingContent.length}`;

  const send = (text: string) => {
    const t = text.trim();
    if (t && !isStreaming && !pendingSql) onSend(t);
  };

  const awaitingDecision = Boolean(pendingSql || pendingCall);

  const renderMessage = (m: CopilotMessage, key: string, within?: React.ReactNode, waiting = false) =>
    m.role === 'user' ? (
      <ChatMessage
        key={key}
        role="user"
        above={m.attachmentIds?.length ? <HistoricalAttachmentChips attachmentIds={m.attachmentIds} /> : undefined}
      >
        {m.content}
      </ChatMessage>
    ) : (
      <AssistantTurn
        key={key}
        message={m}
        place={place}
        onOpenAudit={setAuditId}
        within={within}
        waiting={waiting}
      />
    );

  const renderEntry = (entry: (typeof timeline)[number], i: number) => {
    const key = `${i}-${entry.type}`;
    switch (entry.type) {
      case 'user':
      case 'assistant': {
        const m = messages.find((x) => x.id === entry.messageId);
        return m ? renderMessage(m, key) : null;
      }
      case 'artifact': {
        const art = workspaceTabs.find((t) => t.id === entry.artifactId);
        if (!art) return null;
        return (
          <ArtifactBlock
            key={key}
            artifact={art}
            threadId={threadId}
            onHydrate={hydrate}
            onAddToDashboard={place.features.addToDashboard ? onAddToDashboard : undefined}
          />
        );
      }
      case 'workbook': {
        const workbook = workbooks[entry.id];
        return workbook ? (
          <WorkbookEntry key={key} workbook={workbook} />
        ) : null;
      }
      case 'download': {
        const offer = downloadOffers[entry.id];
        return offer ? <DownloadCard key={key} offer={offer} threadId={threadId} /> : null;
      }
      case 'voice_call': {
        const summary = voiceCallSummaries[entry.id];
        return summary ? <VoiceCallCard key={key} summary={summary} onOpen={openVoiceCall} /> : null;
      }
      case 'sql_approval': {
        // Once decided, the approval has done its job; the answer carries on below.
        if (!pendingSql || pendingSql.timelineId !== entry.id) return null;
        if (!place.features.sqlApproval || !onSQLApproval) {
          return (
            <ApprovalCard
              key={key}
              title="Query needs approval"
              source={pendingSql.connectorName}
              description="Queries can't be approved here."
              actions={onOpenFull && <Button size="small" variant="outlined" onClick={onOpenFull}>Open in Copilot to approve</Button>}
            />
          );
        }
        return <SqlApproval key={key} approval={pendingSql} onDecide={onSQLApproval} />;
      }
      case 'voice_call_approval': {
        if (!pendingCall || pendingCall.timelineId !== entry.id) return null;
        if (place.features.voiceCalls && onVoiceCallApprove && onVoiceCallEditNumber && onVoiceCallReject) {
          return (
            <VoiceCallApprovalCard
              key={key}
              approval={pendingCall}
              onApprove={onVoiceCallApprove}
              onEditNumber={onVoiceCallEditNumber}
              onReject={onVoiceCallReject}
            />
          );
        }
        return (
          <ApprovalCard
            key={key}
            title="Call needs approval"
            source={pendingCall.carrierName}
            description="Calls can only be approved on the Copilot page."
            actions={onOpenFull && <Button size="small" variant="outlined" onClick={onOpenFull}>Open in Copilot</Button>}
          />
        );
      }
      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, position: 'relative' }} {...dropHandlers}>
      {header ?? <ChatHeader title={title} subtitle={subtitle} actions={headerActions} />}
      <FileDropOverlay active={dragging} hint={dropHint} />
      <ChatMessageList
        followKey={followKey}
        scrollToEndKey={`${userTurns}|${pendingSql?.timelineId ?? pendingCall?.timelineId ?? ''}`}
        variant={place.place === 'page' ? 'page' : 'docked'}
        announcement={announcement}
      >
        {isLoadingHistory && messages.length === 0 && <EmptyState message="Loading this conversation…" />}
        {empty && (
          <ChatEmptyState
            title={place.welcome.title || title}
            subtitle={place.welcome.subtitle}
            starters={place.starters}
            onPick={send}
          />
        )}
        {turns.rows.map((row) =>
          row.kind === 'entry'
            ? renderEntry(row.entry, row.i)
            : renderMessage(
                row.message,
                `${row.i}-message`,
                row.within.length ? row.within.map((w) => renderEntry(w.entry, w.i)) : undefined,
              ),
        )}
        {orphans.map((m) => renderMessage(m, `orphan-${m.id}`))}
        {!hasLiveTurn && turns.held &&
          renderMessage(
            turns.held.message,
            `${turns.held.i}-message`,
            turns.held.within.length ? turns.held.within.map((w) => renderEntry(w.entry, w.i)) : undefined,
            awaitingDecision,
          )}
        {!hasLiveTurn && turns.trailing.map((w) => renderEntry(w.entry, w.i))}
        {hasLiveTurn && (
          <ChatMessage role="assistant">
            <ChatWorkSteps
              steps={[...toSteps(turns.held?.message.statuses), ...toSteps(streamingStatuses)]}
              state="live"
              startedAt={streamingStartedAt}
              action={streamStatus.isActive ? streamStatus.primary : undefined}
              note={streamStatus.staleSubtitle ?? undefined}
              renderStep={renderStep}
            />
            {turns.held?.within.map((w) => renderEntry(w.entry, w.i))}
            {turns.trailing.map((w) => renderEntry(w.entry, w.i))}
            {streamingContent && <Markdown>{streamingContent}</Markdown>}
          </ChatMessage>
        )}
      </ChatMessageList>
      <Box sx={{ px: 2, pb: 1.5, pt: 0.5, flexShrink: 0 }}>
        {/* The same column as the conversation, so the two line up. */}
        <Box
          sx={{
            width: '100%',
            maxWidth: place.place === 'page' ? CHAT_READING_WIDTH : 'none',
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
        {composerNotice}
        <ChatComposer
          value={inputText}
          onChange={setInputText}
          onSend={() => send(inputText)}
          onStop={onStop}
          busy={isStreaming}
          disabled={!!pendingSql}
          sendBlockedReason={sendBlockedReason}
          placeholder={
            pendingSql
              ? approvalsInPane ? WAITING_PLACEHOLDER_PANE : WAITING_PLACEHOLDER
              : isStreaming
                ? WORKING_PLACEHOLDER
                : messages.length > 0 ? place.followUpPlaceholder ?? place.placeholder : place.placeholder
          }
          start={composerStart}
          above={
            place.features.contextChips && contextChips ? (
              <>
                <ChatContextChips base={contextChips.base} chips={contextChips.chips} onRemove={contextChips.onRemove} />
                {composerFiles}
              </>
            ) : (
              composerFiles
            )
          }
        />
        {composerFooter}
        </Box>
      </Box>
      <AuditDialog open={!!auditId} auditId={auditId} onClose={() => setAuditId(null)} />
    </Box>
  );
}

/** A finished reply: its folded steps, the results it produced, the answer, and actions. */
function AssistantTurn({
  message,
  place,
  onOpenAudit,
  within,
  waiting = false,
}: {
  message: CopilotMessage;
  place: ChatPlace;
  onOpenAudit: (auditId: string) => void;
  /** Results and cards that arrived while this reply was being worked on. */
  within?: React.ReactNode;
  /** The turn stopped on a decision only the analyst can make. */
  waiting?: boolean;
}) {
  const report = useChatStore((s) => (message.auditId ? s.auditReports[message.auditId] : undefined));
  const stub = useChatStore((s) => (message.auditId ? s.auditStubs[message.auditId] : undefined));

  if (message.type === 'knowledge_captured') return <KnowledgePill items={message.knowledgeItems || []} />;
  if (message.type === 'finding') return null;

  const steps = toSteps(message.statuses);
  const text = message.content?.trim() ?? '';
  const stopped = text.endsWith(STOPPED_MARK);
  const trail = !!message.auditId && copilotAuditHasDataTrail(report, stub);

  return (
    <ChatMessage role="assistant">
      {steps.length > 0 && (
        <ChatWorkSteps
          steps={steps}
          state={stopped ? 'stopped' : waiting ? 'waiting' : 'done'}
          seconds={message.durationSec}
          renderStep={renderStep}
        />
      )}
      {within}
      {text && <Markdown>{message.content}</Markdown>}
      {trail && message.auditId && <ChatSourceNote onOpen={() => onOpenAudit(message.auditId as string)} />}
      {text && <ChatMessageActions onCopy={place.features.copy ? () => copyText(text) : undefined} />}
    </ChatMessage>
  );
}

/**
 * A workbook the agent built: what is in it, a look inside, and a button to
 * take it away.
 *
 * The preview is fetched here rather than in the card, so the kit component
 * stays a thing that draws a workbook it is handed.
 */
function WorkbookEntry({ workbook }: { workbook: Workbook }) {
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<WorkbookPreview | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    setError(null);
    setDownloading(true);
    try {
      await downloadWorkbook(workbook.workbookId, workbook.filename);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The workbook could not be downloaded.');
    } finally {
      setDownloading(false);
    }
  };

  const openPreview = async () => {
    setOpen(true);
    if (preview || loading) return;
    setLoading(true);
    setError(null);
    try {
      setPreview(await fetchWorkbookPreview(workbook.workbookId));
    } catch (e) {
      setOpen(false);
      setError(e instanceof Error ? e.message : 'This workbook could not be previewed.');
    } finally {
      setLoading(false);
    }
  };

  const sheetCount = preview?.sheets.length ?? workbook.sheets.length;

  return (
    <>
      <WorkbookCard
        title={workbook.title}
        sheets={workbook.sheets}
        version={workbook.version}
        error={error}
        onPreview={openPreview}
        onDownload={download}
      />
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={workbook.title}
        subtitle={`${sheetCount} sheet${sheetCount === 1 ? '' : 's'} · v${workbook.version}`}
        size="full"
        busy={loading}
        actions={
          <DownloadButton
            loading={downloading}
            tooltip="Download this workbook as Excel"
            formats={[{ key: 'xlsx', label: 'Excel (.xlsx)', onDownload: download }]}
          />
        }
      >
        {preview && (
          <XlsxPreview sheets={preview.sheets} sheetsOmitted={preview.sheetsOmitted} fill />
        )}
      </Modal>
    </>
  );
}

/** A result in the conversation, shown in full. */
function ArtifactBlock({
  artifact,
  threadId,
  onHydrate,
  onAddToDashboard,
}: {
  artifact: WorkspaceArtifact;
  threadId: string | null;
  onHydrate: (id: string) => void;
  onAddToDashboard?: (artifact: WorkspaceArtifact) => void;
}) {
  const [requested, setRequested] = useState(false);
  const state = getArtifactHydrationState(artifact);

  return (
    <ResultView
      artifact={requested && state === 'stub' ? { ...artifact, hydration: 'loading' } : artifact}
      variant="card"
      threadId={threadId}
      onAddToDashboard={onAddToDashboard}
      onHydrate={(id) => {
        setRequested(true);
        onHydrate(id);
      }}
    />
  );
}
